import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { MemberAddress } from '../../auth/cognito';
import { phoneInputDigitsOnly } from '../../auth/phone';
import './auth.css';

const emptyAddress: MemberAddress = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
};

type AddressField = keyof MemberAddress;

const addressesMatch = (first: MemberAddress, second: MemberAddress) => (
  first.addressLine1 === second.addressLine1
  && first.addressLine2 === second.addressLine2
  && first.city === second.city
  && first.state === second.state
  && first.zipCode === second.zipCode
);

const normalizeAddress = (address: MemberAddress): MemberAddress => ({
  addressLine1: address.addressLine1.trim(),
  addressLine2: address.addressLine2.trim(),
  city: address.city.trim(),
  state: address.state.trim().toUpperCase(),
  zipCode: address.zipCode.trim(),
});

const hasPartialAddress = (address: MemberAddress) => (
  Boolean(
    address.addressLine1
    || address.addressLine2
    || address.city
    || address.state
    || address.zipCode,
  )
);

const hasCompleteAddress = (address: MemberAddress) => (
  Boolean(address.addressLine1 && address.city && address.state && address.zipCode)
);

const Profile = () => {
  const { cancelService, logout, profile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(profile?.name || '');
  const [companyName, setCompanyName] = useState(profile?.companyName || '');
  const [website, setWebsite] = useState(profile?.website || '');
  const [phoneNumber, setPhoneNumber] = useState(profile?.phoneNumber || '');
  const [emailOptIn, setEmailOptIn] = useState(profile?.emailOptIn !== false);
  const [isReseller, setIsReseller] = useState(profile?.isReseller === true);
  const [shippingAddress, setShippingAddress] = useState<MemberAddress>(profile?.shippingAddress || emptyAddress);
  const [billingAddress, setBillingAddress] = useState<MemberAddress>(profile?.billingAddress || emptyAddress);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(
    profile?.billingSameAsShipping === true,
  );
  const [supportNotes, setSupportNotes] = useState(profile?.supportNotes || '');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setName(profile?.name || '');
    setCompanyName(profile?.companyName || '');
    setWebsite(profile?.website || '');
    setPhoneNumber(profile?.phoneNumber || '');
    setEmailOptIn(profile?.emailOptIn !== false);
    setIsReseller(profile?.isReseller === true);
    const nextShippingAddress = profile?.shippingAddress || emptyAddress;
    const nextBillingAddress = profile?.billingAddress || emptyAddress;
    setShippingAddress(nextShippingAddress);
    setBillingAddress(nextBillingAddress);
    setBillingSameAsShipping(
      profile?.billingSameAsShipping === true
      || (
        hasCompleteAddress(nextShippingAddress)
        && addressesMatch(nextShippingAddress, nextBillingAddress)
      ),
    );
    setSupportNotes(profile?.supportNotes || '');
  }, [profile]);

  const updateShippingAddress = (field: AddressField, value: string) => {
    const nextAddress = { ...shippingAddress, [field]: value };
    setShippingAddress(nextAddress);
    if (billingSameAsShipping) {
      setBillingAddress(nextAddress);
    }
  };

  const updateBillingSameAsShipping = (checked: boolean) => {
    setBillingSameAsShipping(checked);
    if (checked) {
      setBillingAddress(shippingAddress);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    const normalizedWebsite = website.trim();
    if (normalizedWebsite) {
      try {
        const parsedWebsite = new URL(normalizedWebsite);
        if (!['http:', 'https:'].includes(parsedWebsite.protocol)) {
          throw new Error('Invalid protocol');
        }
      } catch {
        setError('Enter a valid website URL starting with http:// or https://.');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const normalizedShippingAddress = normalizeAddress(shippingAddress);
      const normalizedBillingAddress = billingSameAsShipping
        ? normalizedShippingAddress
        : normalizeAddress(billingAddress);

      if (hasPartialAddress(normalizedShippingAddress) && !hasCompleteAddress(normalizedShippingAddress)) {
        setError('Enter a complete shipping address or leave it blank.');
        setIsSubmitting(false);
        return;
      }
      if (hasPartialAddress(normalizedBillingAddress) && !hasCompleteAddress(normalizedBillingAddress)) {
        setError('Enter a complete billing address or leave it blank.');
        setIsSubmitting(false);
        return;
      }

      await updateProfile({
        name,
        companyName,
        website: normalizedWebsite,
        phoneNumber,
        emailOptIn,
        isReseller,
        shippingAddress: normalizedShippingAddress,
        billingAddress: normalizedBillingAddress,
        billingSameAsShipping,
        supportNotes,
      });
      setMessage('Your profile was updated.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update your profile.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeCancelDialog = () => {
    if (isCancelling) {
      return;
    }
    setIsCancelDialogOpen(false);
    setCancelError('');
    setConfirmationText('');
  };

  const handleCancelService = async () => {
    setCancelError('');
    if (confirmationText.trim().toLowerCase() !== 'cancel') {
      setCancelError("Type 'cancel' to cancel your service.");
      return;
    }

    setIsCancelling(true);
    try {
      await cancelService(confirmationText);
      navigate('/', {
        replace: true,
        state: { message: 'Your member service has been cancelled.' },
      });
    } catch (requestError) {
      setCancelError(
        requestError instanceof Error
          ? requestError.message
          : 'Unable to cancel service.',
      );
      setIsCancelling(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <section className="auth-shell">
      <div className="auth-hero">
        <p>Member profile</p>
        <h1>Manage your account.</h1>
        <span>Keep your Hogcloud member information current.</span>
      </div>
      <div className="auth-card">
        <div className="profile-summary">
          <strong>{profile?.email}</strong>
          <span>{profile?.emailVerified ? 'Email verified' : 'Email verification pending'}</span>
          <span>Status: {profile?.status || 'ACTIVE'}</span>
        </div>
        <form className="auth-form" onSubmit={handleSubmit}>
          {error && <p className="auth-message error" role="alert">{error}</p>}
          {message && <p className="auth-message success">{message}</p>}
          <div className="auth-field">
            <label htmlFor="profile-name">Full name</label>
            <input
              id="profile-name"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="profile-company">Company Name</label>
            <input
              id="profile-company"
              autoComplete="organization"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="profile-website">Website</label>
            <input
              id="profile-website"
              type="url"
              autoComplete="url"
              placeholder="https://example.com"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="profile-phone">Phone number</label>
            <input
              id="profile-phone"
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
              maxLength={10}
              pattern="[2-9][0-9]{9}"
              placeholder="4795550199"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(phoneInputDigitsOnly(event.target.value))}
            />
          </div>
          <label className="auth-checkbox" htmlFor="profile-email-opt-in">
            <input
              checked={emailOptIn}
              id="profile-email-opt-in"
              onChange={(event) => setEmailOptIn(event.target.checked)}
              type="checkbox"
            />
            <span>Send me account and service emails from Hogcloud</span>
          </label>
          <label className="auth-checkbox" htmlFor="profile-reseller">
            <input
              checked={isReseller}
              id="profile-reseller"
              onChange={(event) => setIsReseller(event.target.checked)}
              type="checkbox"
            />
            <span>I am a reseller for Hogcloud</span>
          </label>
          {isReseller && (
            <p className="auth-disclaimer">
              Your tax ID will be requested. Please fill out the required form stating
              that you are licensed to sell goods and services in Arkansas and email it
              to <a href="mailto:sales@hogcloud.net">sales@hogcloud.net</a>.{' '}
              <a
                href="/assets/docs/AR-Sales-ExemptionCertificate.pdf"
                rel="noopener noreferrer"
                target="_blank"
              >
                Open the Arkansas sales exemption certificate
              </a>
              .
            </p>
          )}
          <fieldset className="auth-fieldset">
            <legend>Shipping address</legend>
            <div className="auth-field">
              <label htmlFor="profile-shipping-address-line-1">Address line 1</label>
              <input
                id="profile-shipping-address-line-1"
                autoComplete="shipping address-line1"
                value={shippingAddress.addressLine1}
                onChange={(event) => updateShippingAddress('addressLine1', event.target.value)}
              />
            </div>
            <div className="auth-field">
              <label htmlFor="profile-shipping-address-line-2">Address line 2</label>
              <input
                id="profile-shipping-address-line-2"
                autoComplete="shipping address-line2"
                value={shippingAddress.addressLine2}
                onChange={(event) => updateShippingAddress('addressLine2', event.target.value)}
              />
            </div>
            <div className="auth-field-grid">
              <div className="auth-field">
                <label htmlFor="profile-shipping-city">City</label>
                <input
                  id="profile-shipping-city"
                  autoComplete="shipping address-level2"
                  value={shippingAddress.city}
                  onChange={(event) => updateShippingAddress('city', event.target.value)}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="profile-shipping-state">State</label>
                <input
                  id="profile-shipping-state"
                  autoComplete="shipping address-level1"
                  maxLength={2}
                  value={shippingAddress.state}
                  onChange={(event) => updateShippingAddress('state', event.target.value)}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="profile-shipping-zip">ZIP code</label>
                <input
                  id="profile-shipping-zip"
                  autoComplete="shipping postal-code"
                  inputMode="numeric"
                  value={shippingAddress.zipCode}
                  onChange={(event) => updateShippingAddress('zipCode', event.target.value)}
                />
              </div>
            </div>
          </fieldset>
          <fieldset className="auth-fieldset">
            <legend>Billing address</legend>
            <label className="auth-checkbox" htmlFor="profile-billing-same-as-shipping">
              <input
                checked={billingSameAsShipping}
                id="profile-billing-same-as-shipping"
                onChange={(event) => updateBillingSameAsShipping(event.target.checked)}
                type="checkbox"
              />
              <span>Billing address is the same as shipping address</span>
            </label>
            <div className="auth-field">
              <label htmlFor="profile-billing-address-line-1">Address line 1</label>
              <input
                id="profile-billing-address-line-1"
                autoComplete="billing address-line1"
                disabled={billingSameAsShipping}
                value={billingAddress.addressLine1}
                onChange={(event) => setBillingAddress({ ...billingAddress, addressLine1: event.target.value })}
              />
            </div>
            <div className="auth-field">
              <label htmlFor="profile-billing-address-line-2">Address line 2</label>
              <input
                id="profile-billing-address-line-2"
                autoComplete="billing address-line2"
                disabled={billingSameAsShipping}
                value={billingAddress.addressLine2}
                onChange={(event) => setBillingAddress({ ...billingAddress, addressLine2: event.target.value })}
              />
            </div>
            <div className="auth-field-grid">
              <div className="auth-field">
                <label htmlFor="profile-billing-city">City</label>
                <input
                  id="profile-billing-city"
                  autoComplete="billing address-level2"
                  disabled={billingSameAsShipping}
                  value={billingAddress.city}
                  onChange={(event) => setBillingAddress({ ...billingAddress, city: event.target.value })}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="profile-billing-state">State</label>
                <input
                  id="profile-billing-state"
                  autoComplete="billing address-level1"
                  disabled={billingSameAsShipping}
                  maxLength={2}
                  value={billingAddress.state}
                  onChange={(event) => setBillingAddress({ ...billingAddress, state: event.target.value })}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="profile-billing-zip">ZIP code</label>
                <input
                  id="profile-billing-zip"
                  autoComplete="billing postal-code"
                  disabled={billingSameAsShipping}
                  inputMode="numeric"
                  value={billingAddress.zipCode}
                  onChange={(event) => setBillingAddress({ ...billingAddress, zipCode: event.target.value })}
                />
              </div>
            </div>
          </fieldset>
          <div className="auth-field">
            <label htmlFor="profile-notes">Service notes</label>
            <textarea
              id="profile-notes"
              value={supportNotes}
              onChange={(event) => setSupportNotes(event.target.value)}
              maxLength={1000}
            />
            <p className="auth-help">Optional notes for future Hogcloud member features or support.</p>
          </div>
          <button className="auth-submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save profile'}
          </button>
          <button className="auth-secondary-action" type="button" onClick={handleLogout}>
            Logout
          </button>
        </form>
        <button
          className="cancel-service-link"
          type="button"
          onClick={() => setIsCancelDialogOpen(true)}
        >
          Cancel service
        </button>
      </div>

      {isCancelDialogOpen && (
        <div className="admin-modal-backdrop" role="presentation" onClick={closeCancelDialog}>
          <div
            className="admin-modal cancel-service-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-service-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="cancel-service-title">Cancel service</h2>
            <p>
              This disables your Hogcloud member login and marks your member profile cancelled.
              Type cancel to confirm.
            </p>
            {cancelError && <p className="auth-message error" role="alert">{cancelError}</p>}
            <div className="auth-field">
              <label htmlFor="cancel-confirmation">Confirmation</label>
              <input
                id="cancel-confirmation"
                autoComplete="off"
                value={confirmationText}
                onChange={(event) => setConfirmationText(event.target.value)}
              />
            </div>
            <div className="admin-modal-actions">
              <button className="auth-link-button" type="button" onClick={closeCancelDialog}>
                Keep service
              </button>
              <button
                className="auth-submit danger"
                type="button"
                onClick={handleCancelService}
                disabled={isCancelling}
              >
                {isCancelling ? 'Cancelling...' : 'Cancel service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Profile;
